import { useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom";
import { CategoryMap } from '../utils/categoryMap';
import './ShopCategory.css'

const apiURL = process.env.REACT_APP_API_URL;

const formatPrice = (value) => {
    const price = Number(value);
    return Number.isFinite(price) ? `${price.toFixed(2).replace('.', ',')}€` : 'Consultar';
};

const ShopCategory = () => {
    const { category } = useParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [brand, setBrand] = useState('all');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [availability, setAvailability] = useState('all');
    const [sort, setSort] = useState('featured');

    useEffect(() => {
        const getCategoryProducts = async () => {
            if (!category) {
                setProducts([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError('');
                const response = await fetch(`${apiURL}/category-products/${encodeURIComponent(category)}`);
                if (!response.ok) {
                    throw new Error('No s’han pogut carregar els productes');
                }
                setProducts(await response.json());
            } catch (fetchError) {
                setError(fetchError.message || 'No s’han pogut carregar els productes');
                setProducts([]);
            } finally {
                setLoading(false);
            }
        }

        getCategoryProducts();
    }, [category]);

    useEffect(() => {
        setBrand('all');
        setMinPrice('');
        setMaxPrice('');
        setAvailability('all');
        setSort('featured');
    }, [category]);

    const brands = useMemo(() => [...new Set(
        products.map((product) => product.brand?.trim()).filter(Boolean)
    )].sort((first, second) => first.localeCompare(second)), [products]);

    const filteredProducts = useMemo(() => {
        const minimum = minPrice === '' ? 0 : Number(minPrice);
        const maximum = maxPrice === '' ? Number.POSITIVE_INFINITY : Number(maxPrice);

        return products
            .filter((product) => {
                const price = Number(product.price);
                const matchesPrice = Number.isFinite(price) && price >= minimum && price <= maximum;
                const matchesBrand = brand === 'all' || product.brand === brand;
                const matchesAvailability = availability === 'all'
                    || (availability === 'stock' && product.inStock)
                    || (availability === 'unavailable' && !product.inStock);
                return matchesPrice && matchesBrand && matchesAvailability;
            })
            .sort((first, second) => {
                if (sort === 'price-asc') return Number(first.price) - Number(second.price);
                if (sort === 'price-desc') return Number(second.price) - Number(first.price);
                if (sort === 'name') return first.name.localeCompare(second.name);
                return Number(second.importance) - Number(first.importance);
            });
    }, [availability, brand, maxPrice, minPrice, products, sort]);

    const clearFilters = () => {
        setBrand('all');
        setMinPrice('');
        setMaxPrice('');
        setAvailability('all');
        setSort('featured');
    };

    return (
        <main className="category-page">
            <header className="category-hero">
                <div>
                    <p className="category-eyebrow">Catàleg d’equipament</p>
                    <h1>{CategoryMap.getLabel(category)}</h1>
                    <p>Troba l’equip que necessites i compara les opcions disponibles.</p>
                </div>
                <span className="category-count">{filteredProducts.length} productes</span>
            </header>

            <section className="category-toolbar" aria-label="Filtres de productes">
                <div className="filter-group">
                    <label htmlFor="brand-filter">Marca</label>
                    <select id="brand-filter" value={brand} onChange={(event) => setBrand(event.target.value)}>
                        <option value="all">Totes les marques</option>
                        {brands.map((availableBrand) => <option key={availableBrand} value={availableBrand}>{availableBrand}</option>)}
                    </select>
                </div>
                <div className="filter-group filter-price">
                    <label>Preu</label>
                    <div className="price-inputs">
                        <input type="number" min="0" placeholder="Mínim" aria-label="Preu mínim" value={minPrice} onChange={(event) => setMinPrice(event.target.value)} />
                        <span aria-hidden="true">-</span>
                        <input type="number" min="0" placeholder="Màxim" aria-label="Preu màxim" value={maxPrice} onChange={(event) => setMaxPrice(event.target.value)} />
                    </div>
                </div>
                <div className="filter-group">
                    <label htmlFor="availability-filter">Disponibilitat</label>
                    <select id="availability-filter" value={availability} onChange={(event) => setAvailability(event.target.value)}>
                        <option value="all">Tots</option>
                        <option value="stock">En estoc</option>
                        <option value="unavailable">No disponible</option>
                    </select>
                </div>
                <div className="filter-group filter-sort">
                    <label htmlFor="sort-filter">Ordenar</label>
                    <select id="sort-filter" value={sort} onChange={(event) => setSort(event.target.value)}>
                        <option value="featured">Destacats</option>
                        <option value="price-asc">Preu: de menor a major</option>
                        <option value="price-desc">Preu: de major a menor</option>
                        <option value="name">Nom</option>
                    </select>
                </div>
                <button type="button" className="clear-filters" onClick={clearFilters}>Netejar</button>
            </section>

            {loading && <p className="category-message">Carregant productes...</p>}
            {error && <p className="category-message category-message--error">{error}</p>}
            {!loading && !error && products.length === 0 && <p className="category-message">Actualment no hi ha productes en aquesta categoria.</p>}
            {!loading && !error && products.length > 0 && filteredProducts.length === 0 && <p className="category-message">No hi ha productes que coincideixin amb aquests filtres.</p>}

            <div className="product-list">
                {filteredProducts.map((product) => (
                    <Link key={product.id} to={`/product/${encodeURIComponent(product.name)}`} className="product-details">
                        <div className="product-image-wrap">
                            <img src={`${product.image}`} alt={product.name} />
                            {product.importance && <span className="product-badge">Destacat</span>}
                        </div>
                        <div className="product-card-content">
                            <p className="product-brand">{product.brand || 'Equipament informàtic'}</p>
                            <h2>{product.name}</h2>
                            <div className="product-card-footer">
                                <strong>{formatPrice(product.price)}</strong>
                                <span className={product.inStock ? 'stock stock--available' : 'stock'}>{product.inStock ? 'En estoc' : 'Consultar'}</span>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </main>
    );
}

export default ShopCategory